import { useEffect, useRef, useState } from "react";
import { PiCaretDownBold, PiCheckBold, PiDownloadSimpleBold, PiFolderOpenBold, PiScanBold, PiXBold } from "react-icons/pi";
import API from "../../api";
import { capture } from "../../instrument";
import toast from "react-hot-toast";
import { DWT_INSTALLER_URL, isScannerServiceMissing, loadScannerList, scanToJpeg, SCANNER_STORAGE_KEY } from "../../utils/dynamsoftWebTwain";

const AddPrescriptionModal = ({ loading = false, admissionSelectedId, setShowAddPrescriptionModal, getAdmissionDetails, setLoading }) => {
  const fileInputRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [formMode, setFormMode] = useState(false);
  const [scanners, setScanners] = useState([]);
  const [scannerName, setScannerName] = useState(() => localStorage.getItem(SCANNER_STORAGE_KEY) || "");
  const [serviceNotInstalled, setServiceNotInstalled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadScannerList().then(({ scanners: nextScanners, scannerName: nextScannerName, serviceNotInstalled: notInstalled }) => {
      if (cancelled) return;
      setScanners(nextScanners);
      setScannerName((current) => (nextScanners.includes(current) ? current : nextScannerName));
      setServiceNotInstalled(notInstalled);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onScannerChange = (event) => {
    const name = event.target.value;
    setScannerName(name);
    localStorage.setItem(SCANNER_STORAGE_KEY, name);
  };

  const submitPrescription = async (file) => {
    if (!admissionSelectedId) return toast.error("Admission sélectionnée non trouvée");
    setLoading(true);
    setShowAddPrescriptionModal(false);

    try {
      const formData = new FormData();
      if (file) formData.append("file", file);
      formData.append("formMode", formMode);
      const response = await API.postFormData(`/admin/admission/${admissionSelectedId}/prescription`, formData);

      if (!response.ok) {
        if (response.code === "NO_VALID_CONTENT_FOUND") toast.error("Aucun contenu valide trouvé dans l'ordonnance");
        else throw new Error(response.message || "Erreur lors de l'ajout de l'ordonnance");
        return;
      }

      await getAdmissionDetails(admissionSelectedId);
      toast.success(file ? "Ordonnance ajoutée et analysée avec succès" : "Ordonnance manuelle créée avec succès");
    } catch (error) {
      capture(error);
      toast.error("Une erreur est survenue lors de l'ajout de l'ordonnance");
    }

    setLoading(false);
  };

  const onScanClick = async () => {
    setScanning(true);
    try {
      const file = await scanToJpeg(scannerName);
      if (!file) {
        toast.error("Aucune page numérisée");
        return;
      }
      await submitPrescription(file);
    } catch (error) {
      if (isScannerServiceMissing(error)) {
        setServiceNotInstalled(true);
        toast.error("Service de numérisation non installé");
      } else {
        capture(error);
        toast.error(error.message || "Erreur lors du scan");
      }
    } finally {
      setScanning(false);
    }
  };

  const busy = loading || scanning;
  const onClose = () => setShowAddPrescriptionModal(false);

  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-black/5">
        <div className="flex items-center justify-between gap-3 px-5 pb-4 pt-4">
          <h2 className="text-base font-semibold text-gray-900">Ajouter une ordonnance</h2>
          <button
            type="button"
            className="-m-1 rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            onClick={onClose}
            disabled={busy}
            aria-label="Fermer"
          >
            <PiXBold className="size-4" />
          </button>
        </div>

        <div className="flex flex-col gap-3 px-5 pb-5">
          <button
            type="button"
            role="checkbox"
            aria-checked={formMode}
            onClick={() => setFormMode((value) => !value)}
            disabled={busy}
            className="flex items-center gap-2.5 text-left disabled:opacity-50"
          >
            <span
              className={`flex size-[18px] shrink-0 items-center justify-center rounded-md border transition-colors ${
                formMode ? "border-primary-500 bg-primary-500 text-white" : "border-gray-300 bg-white text-transparent hover:border-primary-400"
              }`}
            >
              <PiCheckBold className="size-2.5" />
            </span>
            <span className="text-sm font-medium text-gray-700">Ordonnance avec case à cocher</span>
          </button>

          <button
            type="button"
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-50 px-4 py-2 text-sm font-medium text-primary-600 ring-1 ring-primary-100 transition-colors hover:bg-primary-100 disabled:opacity-50"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            <PiFolderOpenBold className="size-4" />
            <span>Depuis un fichier</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,application/pdf"
            className="hidden"
            onChange={(e) => {
              const selectedFile = e.target.files?.[0];
              e.target.value = "";
              if (selectedFile) submitPrescription(selectedFile);
            }}
          />

          {serviceNotInstalled ? (
            <a
              href={DWT_INSTALLER_URL}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              <PiDownloadSimpleBold className="size-4" />
              <span>Installer le driver du scanner</span>
            </a>
          ) : (
            <button
              type="button"
              className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:opacity-50"
              onClick={onScanClick}
              disabled={busy || (scanners.length === 0 && import.meta.env.PROD)}
            >
              <PiScanBold className="size-4" />
              <span>{scanning ? "Scan en cours…" : "Depuis le scanner"}</span>
            </button>
          )}

          {scanners.length > 1 && (
            <div className="-mt-1 flex items-center justify-end px-1 text-xs text-gray-500">
              <div className="relative min-w-0 flex-1">
                <select
                  className="w-full cursor-pointer appearance-none truncate bg-transparent pr-5 text-right text-xs font-medium text-gray-700 underline-offset-2 hover:text-primary-600 hover:underline focus:outline-none disabled:opacity-50"
                  value={scannerName}
                  onChange={onScannerChange}
                  disabled={busy}
                  aria-label="Changer de scanner"
                >
                  {scanners.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center">
                  <PiCaretDownBold className="size-3 text-gray-400" />
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            className="mt-1 self-center text-xs text-gray-500 underline-offset-4 transition-colors hover:text-gray-800 hover:underline disabled:opacity-50"
            onClick={() => submitPrescription(null)}
            disabled={busy}
          >
            ou saisir manuellement
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPrescriptionModal;
